/**
 * Umschalter Deutsch/Englisch und Abgleich mit dem Konto (seit 16.10.2026).
 *
 * `accounts.sprache` steuert die Mails (Server). Die App hält sie gleich mit
 * dem, was jemand sieht: ein Wechsel schreibt die Spalte, und nach der
 * Anmeldung gewinnt die Spalte, außer die Adresse trägt `?lang=`. Die Zeile
 * darf der Kontoinhaber selbst ändern („Users update own account“);
 * accounts_guard_privileged sperrt nur role, plan, email und Co.
 */

import { useEffect } from 'react';
import { supabase } from './supabase';
import { useSitzung, useSitzungOderNull } from './session';
import { setzeSprache, sprache, spracheAusAdresse, spracheAusText, t, useSprache, type Sprache } from './sprache';

async function schreibeKontoSprache(uid: string, s: Sprache) {
  const { error } = await supabase.from('accounts').update({ sprache: s }).eq('supabase_user_id', uid);
  if (error) console.warn('[sprache] Konto nicht aktualisiert', error.message);
}

/** Nach der Anmeldung: Sprache des Kontos übernehmen oder, wenn es keine hat, die der App eintragen. */
export function SpracheAbgleich() {
  const { status, session } = useSitzung();
  const uid = session?.user?.id;
  useEffect(() => {
    if (status !== 'angemeldet' || !uid) return;
    let lebt = true;
    (async () => {
      const { data, error } = await supabase.from('accounts').select('sprache').eq('supabase_user_id', uid).maybeSingle();
      // Ohne Zeile (das Konto entsteht beim ersten Aufruf aus Claude) oder vor der Migration: nichts tun.
      if (!lebt || error || !data) return;
      const konto = spracheAusText(data.sprache);
      const adresse = spracheAusAdresse();
      if (adresse && adresse !== konto) await schreibeKontoSprache(uid, adresse);
      else if (konto && konto !== sprache()) setzeSprache(konto);
      else if (!konto) await schreibeKontoSprache(uid, sprache());
    })();
    return () => { lebt = false; };
  }, [status, uid]);
  return null;
}

/** „DE · EN“: die aktive Sprache fett, die andere als Knopf. */
export function SprachWahl({ className = '' }: { className?: string }) {
  const aktiv = useSprache();
  const session = useSitzungOderNull()?.session;
  const waehle = (s: Sprache) => {
    setzeSprache(s);
    const uid = session?.user?.id;
    if (uid) void schreibeKontoSprache(uid, s);
  };
  return (
    <div role="group" aria-label={t('Sprache', 'Language')} className={`inline-flex items-center gap-1 text-[0.8125rem] ${className}`}>
      {(['de', 'en'] as const).map((s, i) => (
        <span key={s} className="inline-flex items-center gap-1">
          {i > 0 && <span aria-hidden className="text-text-hell">·</span>}
          <button
            type="button"
            lang={s}
            aria-pressed={aktiv === s}
            onClick={() => waehle(s)}
            className={`cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 ${aktiv === s ? 'font-semibold text-tinte' : 'text-text-hell underline decoration-linie underline-offset-[3px] hover:text-tinte'}`}
          >
            {s === 'de' ? 'DE' : 'EN'}
            <span className="sr-only">{s === 'de' ? ' Deutsch' : ' English'}</span>
          </button>
        </span>
      ))}
    </div>
  );
}
